import React, { useState, useEffect, useContext, useRef } from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewInArIcon from '@mui/icons-material/ViewInAr';

import { ListingContext } from '../../contexts/ListingContext';

import CMSForm from '../CMSForm/CMSForm';

import config from '../../config/config';

import './ManageFacilities.css';


const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManageListings() {
  const { getListings, listings, getListingCount, listingCount, searchListing, listingsSearch, publishListing, page, setPage, deleteListing } = useContext(ListingContext)
  const [hidden, setHidden] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [editFormData, setEditFormData] = useState({})
  const searchStringRef = useRef('')
  const publishInFlight = useRef(new Set())
  const deleteInFlight = useRef(false)


  function toggleForm() {
    setHidden(!hidden)
  }

  useEffect(() => {
    getListingCount().catch(() => {})
  }, [])

  useEffect(() => {
    if (searchString !== '') {
      searchListing(searchString).catch(() => {})
    } else {
      getListings(page).catch(() => {})
    }
  }, [page])

  useEffect(() => {
    if (searchStringRef.current === '') setFilteredData([...processData(listings)])
  }, [listings])

  useEffect(() => {
    if (searchString !== '') {
      setFilteredData([...processData(listingsSearch)])
    }
  }, [listingsSearch])

  function processData(data) {
    const newData = data.map(e => createData(e._id, e.name, `${e.address}, ${e.city} ${e.state}`, e.phone, e.published))
    return newData
  }

  function createData(_id, name, address, phone, published) {
    return { _id, name, address, phone, published };
  }

  function handleSearch(e) {
    let string = e.target.value
    searchStringRef.current = string
    setSearchString(string)

    if (string !== '') {
      searchListing(string).catch(() => {})
    }
    else {
      if (page === 1) {
        setFilteredData([...processData(listings)])
        getListings(1).catch(() => {})
      } else {
        setFilteredData([])
        setPage(1)
      }
      getListingCount().catch(() => {})
    }
  }

  function handleCreateNew() {
    setEditFormData({})
    toggleForm()
  }

  function handleEditClick(id) {
    let selected = getCurrListData((searchString !== '' ? listingsSearch : listings).findIndex((f) => String(f._id) === String(id)))
    if (!selected) return
    setEditFormData({ ...selected })
    toggleForm()
  }

  function handleSave() {
    toggleForm();
  }

  async function handlePublish(id) {
    let selected = getCurrListData((searchString !== '' ? listingsSearch : listings).findIndex((f) => String(f._id) === String(id)))
    if (!selected) return
    const inFlightId = String(id)
    if (publishInFlight.current.has(inFlightId)) return
    publishInFlight.current.add(inFlightId)
    try {
      await publishListing(selected._id, !selected.published, searchString)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      publishInFlight.current.delete(inFlightId)
    }
  }

  function handleOpenPreview(id) {
    let selected = getCurrListData((searchString !== '' ? listingsSearch : listings).findIndex((f) => String(f._id) === String(id)))
    if (!selected) return
    window.open(`${config.appUrl}/facility-preview/${selected.url_title ? selected.url_title : selected._id}`)
  }

  function getCurrListData(i) {
    if (searchString !== '') {
      return listingsSearch[i]
    }
    else {
      return listings[i]
    }
  }

  async function handleDelete() {
    if (deleteInFlight.current) {
      setDeleteConfirm(null)
      return
    }
    deleteInFlight.current = true
    try {
      let selected = getCurrListData((searchString !== '' ? listingsSearch : listings).findIndex((f) => String(f._id) === String(deleteConfirm)))
      if (!selected) {
        setDeleteConfirm(null)
        return
      }
      setDeleteConfirm(null)
      await deleteListing(selected._id, searchString)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      deleteInFlight.current = false
    }
  }

  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => { handleSearch(e) }}
                value={searchString}
              />
            </Search>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={handleCreateNew} variant="contained">Create New</Button>
            </div>
          </Toolbar>
        </AppBar>
      </Box>

      {/* form */}
      {hidden && <CMSForm
        initialData={editFormData}
        closeForm={() => { toggleForm() }}
        searchString={searchString}
        page={page}
        onSave={handleSave}
      />}


      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, i) => (
              <TableRow
                key={row._id ? String(row._id) : row.name + i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>

                    <TableOption
                      color={'inherit'}
                      name={filteredData[i].published ? 'Unpublsih' : 'Publish'}
                      onClick={() => { handlePublish(row._id) }}
                    >
                      {filteredData[i].published ?
                        <RemoveRedEyeIcon />
                        :
                        <VisibilityOffIcon />
                      }
                    </TableOption>

                    <TableOption
                      color={'primary'}
                      name={'Edit'}
                      onClick={() => { handleEditClick(row._id) }}
                    >
                      <EditIcon />
                    </TableOption>

                    <TableOption
                      color={'primary'}
                      name={'View Preview'}
                      onClick={() => { handleOpenPreview(row._id) }}
                    >
                      <ViewInArIcon />
                    </TableOption>

                    <TableOption
                      color={'warning'}
                      name={'Delete'}
                      onClick={() => {
                        if (!row._id) return
                        setDeleteConfirm(row._id)
                      }}
                    >
                      <DeleteIcon />
                    </TableOption>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {searchString === '' && listingCount > 15 &&
        <div style={{ width: '100%', margin: '40px 0px', display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2}>
            <Pagination page={page} onChange={(e, i) => { setPage(i) }} count={Math.ceil(listingCount / 15)} showFirstButton={listingCount / 15 > 5 ? true : false} showLastButton={listingCount / 15 > 5 ? true : false} />
          </Stack>
        </div>
      }

      <Dialog
        open={deleteConfirm !== null}
        onClose={() => { setDeleteConfirm(null) }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`delete listing`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete ${(searchString !== '' ? listingsSearch : listings).find((f) => String(f._id) === String(deleteConfirm))?.name || ''}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirm(null) }}>Cancel</Button>
          <Button onClick={() => { handleDelete() }} autoFocus>
          Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ManageListings

const TableOption = ({ children, name, color, onClick }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: 'pointer' }} onClick={onClick}>
      <Tooltip title={name}>
        <IconButton color={color}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  )
}